import type { RenderStatusType } from '@/shared/constants/status';
import type { ClassTypes, EnvironmentSetting } from '@/shared/constants';

/**
 * Explicit render type classification
 * Replaces implicit logic based on scenario_type, source_render_id, etc.
 */
export enum RenderType {
  INTERMEDIATE_PHOTOREALISTIC = 'intermediate_photorealistic',
  FINAL_PHOTOREALISTIC = 'final_photorealistic',
  FINAL_STYLED = 'final_styled',
  UPSCALED = 'upscaled',
  CONTROL_GENERATION = 'control_generation',
  TEST = 'test',
}

/**
 * Helper to check if render type is user-visible
 */
export function isUserVisibleRenderType(
  renderType?: RenderType | string
): boolean {
  return [
    RenderType.FINAL_PHOTOREALISTIC,
    RenderType.FINAL_STYLED,
    RenderType.UPSCALED,
  ].includes(renderType as RenderType);
}

/**
 * Helper to check if render is intermediate (hidden from user)
 */
export function isIntermediateRender(
  renderType?: RenderType | string
): boolean {
  return renderType === RenderType.INTERMEDIATE_PHOTOREALISTIC;
}

/**
 * Helper to check if render is final (visible to user)
 */
export function isFinalRender(renderType?: RenderType | string): boolean {
  return [RenderType.FINAL_PHOTOREALISTIC, RenderType.FINAL_STYLED].includes(
    renderType as RenderType
  );
}

// Type for render prompts (declared before use)
export interface RenderPrompts {
  prompt_concept?: string;
  prompt_angle?: string;
}

// Type for control in render settings
export interface RenderControl {
  id: string;
}

/**
 * Named model family — the orthogonal "which model" axis (independent of
 * Effort). AURORA = Gemini-led (true-to-photo), ATLAS = OpenAI-led (stylized).
 * Default AURORA. Wire field: `render_model_family` (snake_case in attributes).
 */
export type RenderModelFamily = 'AURORA' | 'ATLAS';

/**
 * Render effort — the orthogonal "how hard/fast/expensive" axis (independent
 * of the model family). Controls speed/quality/cost. Default STANDARD.
 * Wire field: `render_effort` (snake_case in attributes).
 */
export type RenderEffort = 'BRAINSTORM' | 'STANDARD' | 'HIGH';

/**
 * Design ambition — how far the new design may travel from the house standing
 * today. A taste axis, orthogonal to effort: effort buys model quality,
 * ambition buys boldness. BALANCED is the untouched middle (the server adds
 * nothing to the prompt for it) and is what an absent value means.
 * Wire field: `render_ambition` (snake_case in attributes).
 */
export type RenderAmbition = 'MODEST' | 'BALANCED' | 'AMBITIOUS';

/**
 * Render mode: explore (default — start from the angle photo) or refine
 * (iterate on a previous render specified by parent_render_id). Mirrors
 * the API enum and switches both the pipeline input image and the LLM
 * prompt template.
 */
export type RenderMode = 'explore' | 'refine';

// Type for render settings
export type RenderSettings = {
  version?: number;
  seed?: number;
  prompts?: RenderPrompts;
  style?: string;
  color?: string;
  material?: string;
  controls?: RenderControl[] | null;
  references?: string[] | { id: string }[] | null;
  // API may return these fields too
  style_ids?: number[];
  material_ids?: number[];
  colors?: string[]; // Unified: null (auto), ["palette:1"] (palette), or ["#FF0000", "#00FF00"] (custom)
  // Real manufacturer products (Brands): ["siding:color:88@double-4-dutchlap"],
  // ["paint:brand:3"]. Absent on renders made before Brands became a setting —
  // those kept their picks inside `colors`, see migrateLegacyProductPicks.
  brand_selections?: string[];
  render_model_family?: RenderModelFamily; // Named model selection (Aurora/Atlas)
  render_effort?: RenderEffort; // Effort tier (brainstorm/standard/high)
  render_ambition?: RenderAmbition; // Design ambition (modest/balanced/ambitious)
  // Project-level context snapshotted into the render at create time. The form
  // never sends these (the client reads live project goals/region elsewhere),
  // but the immutable snapshot carries them so the read-only "View details"
  // sheet can show the historical Tasks / Construction region used for this
  // render — matching Apple/Android. project_goals_ai is server-only.
  project_goals?: string | null;
  construction_region?: string | null;
  // Project references by category (arrays of UUIDs)
  style_project_reference_ids?: string[];
  material_project_reference_ids?: string[];
  color_project_reference_ids?: string[];
  output_project_reference_ids?: string[];
  // Auto-use project references flags. There is no `use_auto_output_refs`:
  // OUTPUT references are never auto-injected — they only feed the StyleTransfer
  // step, and only when the user explicitly picks one via output_project_reference_ids.
  use_auto_style_refs?: boolean;
  use_auto_material_refs?: boolean;
  use_auto_color_refs?: boolean;
  // Refine mode: when 'refine', parent_render_id supplies the pipeline input
  // image and the LLM switches to a refinement-specific template.
  mode?: RenderMode;
  parent_render_id?: string | null;
  // Hi-Res toggle (~2K native output via provider quality=high). Snapshot on
  // `render.settings` so the immutable "how this render was produced" is
  // preserved. Gated by `is_feature_hires_render` on the plan.
  is_hires?: boolean;
  // Extended thinking toggle. When true, the design-prompt LLM uses a
  // stronger reasoning model. Snapshot on `render.settings` so the
  // immutable "how this render was produced" is preserved. Gated by
  // `is_feature_extended_thinking` on the plan.
  is_extended_thinking?: boolean;
  [key: string]: unknown; // Allow additional fields from API
};

export interface Render {
  type: typeof ClassTypes.RENDER;
  id: string;
  attributes: {
    status: RenderStatusType;
    render_type?: RenderType; // Explicit render classification
    priority: number;
    settings: RenderSettings;
    setup_hash: string;
    render_provider?: string;
    started_at?: string;
    completed_at?: string;
    created_at: string;
    updated_at: string;
    error_message?: string;
    typical_duration?: number; // Estimated duration in seconds
    duration_predicted?: number; // Predicted duration for this specific render
    is_upscaled?: boolean; // Whether the render has been upscaled to higher quality
    file_url?: string; // Direct URL to the rendered image (included by API for convenience)
    llm_feedback?: LlmFeedback; // User-facing LLM feedback (available in all environments)
    mode?: RenderMode;
    is_refine?: boolean;
  };
  relationships?: {
    concept_angle?: {
      data: {
        type: 'concept_angle';
        id: string;
      };
      meta?: {
        concept_id: string;
        concept_sequence_number?: number;
      };
    };
    source_render?: {
      data: {
        type: typeof ClassTypes.RENDER;
        id: string;
      };
    };
    parent_render?: {
      data: {
        type: typeof ClassTypes.RENDER;
        id: string;
      } | null;
      meta?: {
        concept_id?: string;
        concept_angle_id?: string;
        file_url?: string;
      };
    };
    root_render?: {
      data: {
        type: typeof ClassTypes.RENDER;
        id: string;
      } | null;
    };
    file?: {
      data: {
        type: 'files';
        id: string;
      } | null;
      meta?: {
        url: string;
      };
    };
    references?: {
      data: Array<{
        type: 'references';
        id: string;
      }>;
    };
  };
}

/**
 * User-facing LLM feedback (positive feedback, filtered content warnings)
 * Available in all environments, not just debug mode
 */
export interface LlmFeedback {
  positive_feedback?: string;
  filtered_content?: string[];
}

export interface RenderCreateRequest {
  data: {
    type: typeof ClassTypes.RENDER;
    attributes: {
      version: number;
      project_goals?: string | null;
      construction_region?: string | null;
      seed?: number;
      style_ids?: number[] | null;
      material_ids?: number[] | null;
      colors?: string[] | null; // Unified: null (auto), ["palette:1"] (palette), or ["#FF0000", "#00FF00"] (custom)
      // Real manufacturer products at brand, line or color precision. Only the
      // explicit three-segment grammar is accepted here.
      brand_selections?: string[] | null;
      controls?: RenderControl[] | null;
      prompts: RenderPrompts;
      output_style_id?: number | null;
      environment_setting?: EnvironmentSetting;
      render_model_family?: RenderModelFamily; // Named model selection (Aurora/Atlas)
      render_effort?: RenderEffort; // Effort tier (brainstorm/standard/high)
      render_ambition?: RenderAmbition; // Design ambition (modest/balanced/ambitious)
      // Project references by category (arrays of UUIDs)
      style_project_reference_ids?: string[] | null;
      material_project_reference_ids?: string[] | null;
      color_project_reference_ids?: string[] | null;
      output_project_reference_ids?: string[] | null;
      // Auto-use project references flags
      use_auto_style_refs?: boolean;
      use_auto_material_refs?: boolean;
      use_auto_color_refs?: boolean;
      // Refine mode — server enforces required_if(mode=refine) for parent_render_id
      mode?: RenderMode;
      parent_render_id?: string | null;
      // Hi-Res rendering toggle (~2K native output, Pro+ only). Server
      // rejects a bypassed payload with PACK_REQUIRED (422).
      is_hires?: boolean;
      // Extended thinking toggle: stronger reasoning model for the design
      // prompt (Pro+ only). Server rejects a bypassed
      // payload with PACK_REQUIRED (422).
      is_extended_thinking?: boolean;
    };
  };
}

export interface RenderCreateResponse {
  data: Render;
}

/**
 * Server-authoritative reason the user cannot render right now. The API
 * applies priority server-side (tokens > queue) and emits exactly one of
 * these alongside `can_render`. `null` means rendering is allowed.
 *
 * - `insufficient_tokens` — balance can't cover the next render (legacy
 *   token users only post-pivot).
 * - `queue_full` — cross-device render slots are at capacity.
 * - `subscription_required` — dormant: free tier renders post-pivot, so the
 *   server won't emit it now, but the contract keeps it for future gating.
 * - `free_designs_exhausted` — the free-tier designs allowance is spent.
 * - `pack_designs_exhausted` — the building's pack design cap is spent
 *   (emitted only by the project-scoped REST endpoint; the user-level WS
 *   aggregate never carries it).
 *
 * NOTE: the WS aggregate is user-level by design — on a pack building it can
 * report a start-gate reason (insufficient_tokens / free_designs_exhausted)
 * that the building's pack actually lifts. POST is authoritative there; see
 * the pack-context skip in `use-render-preflight.ts`.
 *
 * The frontend MUST NOT recompute this; it only maps the server value to the
 * matching paywall (see `use-render-preflight.ts`).
 */
export type RenderBlockReason =
  | 'insufficient_tokens'
  | 'queue_full'
  | 'subscription_required'
  | 'free_designs_exhausted'
  | 'pack_designs_exhausted'
  | null;

export interface QueueStatus {
  type: 'queue-status';
  attributes: {
    queue_limit: number;
    active_renders: number;
    available_slots: number;
    renders: {
      pending: number;
      queued: number;
      processing: number;
    };
    can_render: boolean;
    block_reason: RenderBlockReason;
  };
}

export interface QueueStatusResponse {
  data: QueueStatus;
}
